import classcat from "classcat";
import React from "react";

import { Checkbox } from "@touchtech/baselayer-ui";
import { Button, IconSearch } from "@touchtech/web-baselayer-ui";

import CollectionModal from "@is/components/CollectionModal";
import DrawerActionButton, {
  ActionButtonVariant,
} from "@is/components/DrawerActionButton/DrawerActionButton";

import CollectionList from "../CollectionList";
import SearchStoreFavorites from "../SearchStoreFavorites";
import css from "./CollectionEditView.module.css";
import useManageCollectionEditView from "./hooks/useManageCollectionEditView";

type Props = {
  collectionId: string;
  onClose: () => void;
  onRemove: () => void;
};

const CollectionEditView = ({ collectionId, onClose, onRemove }: Props) => {
  const {
    t,
    isPending,
    collection,
    collectionName,
    storedCollectionName,
    isCollectionModalOpen,
    includedStyleVariants,
    searchAreaRef,
    searchFieldRef,
    searchContentRef,
    isSearchVisible,
    showSearchButton,
    isDemoCollection,
    isCheckoutSuggestionsCollection,
    handleCollectionModalOpen,
    handleCollectionModalClose,
    handleNameChange,
    handleCollectionSave,
    handleCollectionReorder,
    handleCollectionRemove,
    handleCollectionRemoveStyle,
    handleDemoToggle,
    handleCheckoutSuggestionsToggle,
    getStyles,
    addStyleToCollection,
    scrollToItem,
    hasNameDifference,
    isDiscoverYourOutfitsEnabled,
    isBrandWide,
  } = useManageCollectionEditView(collectionId, onRemove);

  const showStartScreenOption = isDiscoverYourOutfitsEnabled && !isBrandWide;
  /* Deliberately not gated on discoverYourOutfits: that flag belongs to the start screen feature,
     and checkout suggestions must stay available to stores that do not use it. Still withheld for
     brand-wide collections, which carry no edit affordances at all. */
  const showCheckoutSuggestionsOption = !isBrandWide;

  return (
    <div className={css.container}>
      {/* Brand-wide collections get Back only — no edit pencil, since renaming them here is
          refused by the backend and is the admin dashboard's job. */}
      {isBrandWide ? (
        <DrawerActionButton
          onClick={onClose}
          variant={ActionButtonVariant.Back}
        />
      ) : (
        <DrawerActionButton
          onClick={handleCollectionModalOpen}
          variant={ActionButtonVariant.Edit}
          onSecondaryButtonClick={onClose}
          secondaryVariant={ActionButtonVariant.Back}
        />
      )}
      <div className={css.headerName}>
        <h1 className="text-headline-5xl">{storedCollectionName}</h1>
      </div>
      {/* The two options share one group so they read as a pair: the section places the pair on
          the right, and the group gives the rows a common left edge so the checkboxes line up.
          Right-aligning each row on its own instead lines up their right edges, which pushed the
          longer label's checkbox further left. */}
      {(showStartScreenOption || showCheckoutSuggestionsOption) && (
        <div className={css.checkboxSection}>
          <div className={css.checkboxGroup}>
            {showStartScreenOption && (
              <div className={css.checkboxContainer}>
                <Checkbox
                  checked={isDemoCollection}
                  onChange={handleDemoToggle}
                />
                <span className={classcat([css.showInStock, "text-label-lg"])}>
                  {t("Use as start screen")}
                </span>
              </div>
            )}
            {showCheckoutSuggestionsOption && (
              <div className={css.checkboxContainer}>
                <Checkbox
                  checked={isCheckoutSuggestionsCollection}
                  onChange={handleCheckoutSuggestionsToggle}
                />
                <span className={classcat([css.showInStock, "text-label-lg"])}>
                  {t("Use for checkout suggestions")}
                </span>
              </div>
            )}
          </div>
        </div>
      )}
      <div className={css.content}>
        <div className={css.collectionStyles}>
          {collection && (
            <CollectionList
              collection={collection}
              onRemove={handleCollectionRemoveStyle}
              onReorder={handleCollectionReorder}
              readOnly={isBrandWide}
            />
          )}
        </div>

        {!isBrandWide && (
          <div className={css.search}>
            <div ref={searchAreaRef} className={css.searchAreaStart} />
            <SearchStoreFavorites
              onStylesGet={getStyles}
              isPending={isPending}
              onStyleToCollectionAdd={addStyleToCollection}
              includedVariants={includedStyleVariants}
              onSearchFocus={scrollToItem}
              searchRef={searchFieldRef}
              searchContentRef={searchContentRef}
            />
          </div>
        )}
      </div>

      <div
        className={classcat([
          "text-label-lg",
          css.searchButtonContainer,
          {
            [css.visible]: !isBrandWide && showSearchButton && !isSearchVisible,
          },
        ])}
      >
        <Button
          className={css.searchButton}
          variant="secondary"
          sizing="lg"
          width="full"
          onClick={scrollToItem}
        >
          <IconSearch /> {t("Search and add")}
        </Button>
      </div>

      {!isBrandWide && (
        <CollectionModal
          title={t("Edit collection")}
          isOpen={isCollectionModalOpen}
          onClose={handleCollectionModalClose}
          value={collectionName}
          onChange={handleNameChange}
          onPrimaryButtonClick={handleCollectionSave}
          onSecondaryButtonClick={handleCollectionModalClose}
          onCollectionRemove={handleCollectionRemove}
          isEditMode={true}
          hasNameDifference={hasNameDifference}
        />
      )}
    </div>
  );
};

export default CollectionEditView;
